import React, { useState, useRef, useEffect } from 'react';
import { useController, Control, ControllerProps } from 'react-hook-form';
import { cn } from 'lib/utils';

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  name: string;
  control: Control<any>;
  options: MultiSelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  rules?: ControllerProps['rules'];
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  name,
  control,
  options,
  placeholder = 'Select options...',
  className,
  disabled = false,
  rules,
}) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: [],
    rules,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedValues = field.value || [];

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const toggleOption = (value: string) => {
    let newValues: string[];

    if (selectedValues.includes(value)) {
      // Deselecting the option
      newValues = selectedValues.filter((v: string) => v !== value);
    } else {
      // Selecting the option
      if (value === 'all') {
        // If "All" is selected, clear all other selections
        newValues = ['all'];
      } else {
        // If any other option is selected, remove "All" if it was selected
        newValues = [...selectedValues.filter((v: string) => v !== 'all'), value];
      }
    }

    field.onChange(newValues);
  };

  const removeOption = (value: string) => {
    const newValues = selectedValues.filter((v: string) => v !== value);
    field.onChange(newValues);
  };

  const getSelectedLabel = (value: string) => {
    return options.find((option) => option.value === value)?.label || value;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      <div
        className={cn(
          'flex flex-wrap gap-2 p-3 border rounded-md bg-white shadow-sm cursor-pointer min-h-[48px] items-center',
          'focus-within:outline-none focus-within:ring-2 focus-within:ring-primary',
          disabled && 'opacity-50 cursor-not-allowed',
          error && 'border-destructive',
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {selectedValues.length === 0 ? (
          <span className="text-[#989797] text-xs">{placeholder}</span>
        ) : (
          selectedValues.map((value: string) => (
            <span
              key={value}
              className="inline-flex items-center gap-1 px-3 py-1 bg-badge text-black rounded-md text-xs"
            >
              {getSelectedLabel(value)}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeOption(value);
                }}
                className="text-black/80 hover:text-black ml-1"
                disabled={disabled}
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          <div className="p-2">
            <input
              type="text"
              placeholder="Search options..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border text-sm border-gray-300 rounded-md bg-white text-black placeholder:text-[#989797] focus:outline-none focus:ring-2 focus:ring-purple-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {filteredOptions.length === 0 ? (
            <div className="p-2 text-[#64748B] text-center">No options found</div>
          ) : (
            <div className="max-h-48 overflow-y-auto">
              {filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className="px-3 py-2 hover:bg-gray-100 text-sm cursor-pointer flex items-center"
                  onClick={() => toggleOption(option.value)}
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    onChange={() => {}}
                    className="mr-2 text-sm"
                  />
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {error && <p className="text-sm text-destructive mt-1">{error.message}</p>}
    </div>
  );
};

export { MultiSelect };
